import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Edit, Trash2 } from 'lucide-react';
import AssessmentForm from '../forms/assessment-form';

export default function AssessmentsTab({
  selectedCourse,
  allAssessments,
  setAllAssessments,
  standaloneCategory,
  filterCategory,
}) {
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Filter assessments based on the selected course or standalone category
  const assessments = selectedCourse
    ? allAssessments.filter((a) => a.courseId === selectedCourse.id)
    : allAssessments.filter((a) => (
      !a.courseId
      && (filterCategory ? a.category === filterCategory : true)
    ));

  const handleAddAssessment = () => {
    setEditingAssessment(null);
    setShowForm(true);
  };

  const handleEditAssessment = (assessment) => {
    setEditingAssessment(assessment);
    setShowForm(true);
  };

  const handleDeleteAssessment = (id) => {
    setAllAssessments(allAssessments.filter((a) => a.id !== id));
  };

  const handleSaveAssessment = (newAssessment) => {
    if (newAssessment.id) {
      setAllAssessments(allAssessments.map((a) => (a.id === newAssessment.id ? newAssessment : a)));
    } else {
      const courseId = selectedCourse ? selectedCourse.id : null;
      const category = !selectedCourse ? (standaloneCategory || filterCategory || '') : undefined;
      setAllAssessments([
        ...allAssessments,
        {
          ...newAssessment,
          id: `a${allAssessments.length + 1}`,
          courseId,
          ...(category ? { category } : {}),
        },
      ]);
    }
    setShowForm(false);
  };

  const handleCancelForm = () => {
    setShowForm(false);
  };

  const cardClass = 'bg-white rounded-lg shadow p-6';
  const cardHeaderClass = 'flex flex-row items-center justify-between mb-4';
  const cardTitleClass = 'text-2xl font-bold text-gray-800';
  const buttonClass = 'px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 flex items-center';
  const tableClass = 'min-w-full divide-y divide-gray-200';
  const tableHeaderClass = 'bg-gray-900';
  const tableHeadClass = 'px-6 py-3 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider';
  const tableCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-gray-900';
  const actionButtonClass = 'p-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500';

  if (showForm) {
    return (
      <AssessmentForm
        assessment={editingAssessment}
        onSave={handleSaveAssessment}
        onCancel={handleCancelForm}
        courseId={selectedCourse?.id}
      />
    );
  }

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h2 className={cardTitleClass}>
          {selectedCourse
            ? `Assessments for ${selectedCourse.name}`
            : `Standalone Assessments${(standaloneCategory || filterCategory) ? ` - ${(standaloneCategory || filterCategory)}` : ''}`}
        </h2>
        <button type="button" onClick={handleAddAssessment} className={buttonClass}>
          <Plus className="mr-2 h-4 w-4" />
          {' '}
          Add New Assessment
        </button>
      </div>
      <div>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead className={tableHeaderClass}>
              <tr>
                <th scope="col" className={tableHeadClass}>Name</th>
                <th scope="col" className={tableHeadClass}>Description</th>
                <th scope="col" className={tableHeadClass}>Total Questions</th>
                <th scope="col" className={tableHeadClass}>Questions to Present</th>
                <th scope="col" className={`${tableHeadClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assessments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    {selectedCourse ? 'No assessments for this course yet.' : 'No standalone assessments yet.'}
                  </td>
                </tr>
              ) : (
                assessments.map((assessment) => (
                  <tr key={assessment.id} className="odd:bg-white even:bg-gray-50">
                    <td className={`${tableCellClass} font-medium`}>{assessment.name}</td>
                    <td className={tableCellClass}>{assessment.description}</td>
                    <td className={tableCellClass}>{assessment.questionCount}</td>
                    <td className={tableCellClass}>{assessment.questionsToPresent}</td>
                    <td className={`${tableCellClass} text-right`}>
                      <button type="button" className={actionButtonClass} onClick={() => handleEditAssessment(assessment)}>
                        <Edit className="h-4 w-4 text-gray-700" />
                        <span className="sr-only">Edit</span>
                      </button>
                      <button type="button" className={actionButtonClass} onClick={() => handleDeleteAssessment(assessment.id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                        <span className="sr-only">Delete</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
AssessmentsTab.propTypes = {
  selectedCourse: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  allAssessments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      courseId: PropTypes.string,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      questionCount: PropTypes.number.isRequired,
      questionsToPresent: PropTypes.number.isRequired,
    }),
  ).isRequired,
  setAllAssessments: PropTypes.func.isRequired,
  standaloneCategory: PropTypes.string,
  filterCategory: PropTypes.string,
};

AssessmentsTab.defaultProps = {
  selectedCourse: null,
  standaloneCategory: '',
  filterCategory: '',
};
